import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Trash2, ExternalLink } from 'lucide-react';
import { getWishlist, removeFromWishlist } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const Wishlist = () => {
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchWishlist();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchWishlist = async () => {
    try {
      const response = await getWishlist();
      setWishlist(response.data);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromWishlist(productId);
      setWishlist(wishlist.filter((item) => item.product.id !== productId));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove from wishlist');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="text-center">
          <Heart size={64} className="text-white/20 mx-auto mb-6" />
          <h1 className="font-display text-4xl uppercase mb-4">WISHLIST</h1>
          <p className="text-white/60 mb-8">Sign in to save your favorite kicks</p>
          <Link to="/login">
            <Button className="btn-primary">SIGN IN</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-6 lg:px-12" data-testid="wishlist-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-display text-5xl sm:text-6xl uppercase mb-4">MY WISHLIST</h1>
          <p className="text-white/60">{wishlist.length} items saved</p>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="aspect-square skeleton" />
            ))}
          </div>
        ) : wishlist.length === 0 ? (
          <div className="text-center py-24">
            <Heart size={64} className="text-white/20 mx-auto mb-6" />
            <p className="text-white/60 text-lg mb-8">Your wishlist is empty</p>
            <Link to="/shop">
              <Button className="btn-primary">START SHOPPING</Button>
            </Link>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {wishlist.map((item) => (
              <div key={item.wishlist_id} className="card-product group" data-testid={`wishlist-item-${item.product.id}`}>
                <Link to={`/product/${item.product.id}`}>
                  <div className="product-image-container bg-[#121212] aspect-square">
                    <img
                      src={item.product.image_url}
                      alt={item.product.name}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Tags */}
                    <div className="absolute top-3 left-3 flex gap-2">
                      {item.product.is_hot && <span className="tag-hot">HOT</span>}
                      {item.product.is_limited && <span className="tag-limited">LIMITED</span>}
                    </div>
                  </div>
                </Link>

                <div className="p-4">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1">{item.product.brand}</p>
                  <Link to={`/product/${item.product.id}`}>
                    <h3 className="text-white font-medium text-sm mb-2 group-hover:text-[#D2FF00] transition-colors">
                      {item.product.name}
                    </h3>
                  </Link>
                  
                  <div className="flex items-center justify-between">
                    <span className="price-current text-lg">${item.product.price.toFixed(2)}</span>
                    
                    <div className="flex gap-2">
                      <a
                        href={item.product.affiliate_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-[#D2FF00] text-black hover:bg-white transition-colors"
                        title="Buy Now"
                      >
                        <ExternalLink size={16} />
                      </a>
                      <button
                        onClick={() => handleRemove(item.product.id)}
                        className="p-2 bg-white/10 text-white hover:bg-[#FF3B30] transition-colors"
                        title="Remove"
                        data-testid={`remove-wishlist-${item.product.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;
